import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { LegalFooterNav, LegalHero } from "@/components/Legal";

const SECTIONS = [
  {
    id: "general",
    title: "General",
    items: [
      ["What is ADIT Connect?",
        "ADIT Connect is a B2B industrial specification, sourcing and RFQ platform. It normalises technical product data so you can search on the parameters that actually matter, find equivalent products across manufacturers, issue structured requests for quotation, and compare the technical and commercial offers you receive side by side."],
      ["Who can use ADIT Connect?",
        "Commercial entities: EPC contractors, operators, plant and facility owners, consultants and procurement teams on the buying side; and manufacturers, authorised distributors, stockists and traders on the supply side. It is not a consumer marketplace."],
      ["Is ADIT Connect a manufacturer or supplier?",
        "No. ADIT Connect normally acts as the platform that connects buyers with independent manufacturers, distributors, stockists and suppliers. We are not automatically the manufacturer, seller, importer, technical guarantor or delivery guarantor. The only exception is a Managed by ADIT transaction that ADIT has separately reviewed and expressly accepted."],
      ["What industrial products does the platform cover?",
        "The catalogue focuses on industrial process, mechanical and electrical materials used in oil and gas, EPC, power, water, marine and general industry — for example valves, fittings, flanges, instrumentation, pumps, seals, electrical and control items, and related consumables. Coverage expands as manufacturers and suppliers add verified product data."],
      ["Is registration required?",
        "You can search specifications and submit an RFQ as a guest. An account is needed to keep your enquiries in one place, receive and compare quotations, message suppliers, track orders and download comparison exports. Suppliers must register to receive enquiries and quote."],
    ],
  },
  {
    id: "spec-finder",
    title: "Spec Finder",
    items: [
      ["What is Spec Finder?",
        "Spec Finder is the platform's product search. It combines catalogue search with built-in cross-referencing, so a search returns both the item you asked for and the technically comparable products from other manufacturers, with every parameter set out for review."],
      ["How do I search by manufacturer or part number?",
        "Enter a manufacturer, brand or part number in the search field on the Product Search page. You can then narrow the results with the structured technical filters — category, manufacturer, size, rating, material and other parameters — instead of relying on free text alone."],
      ["What does \"equivalent\" mean?",
        "An equivalent is a product from another manufacturer whose normalised technical parameters are comparable to the item you searched for. The platform shows each parameter individually, marked as meeting the requirement, showing a technical deviation, or not meeting the requirement. There is no single percentage score, because a single mismatched parameter can rule a product out entirely."],
      ["Does ADIT Connect guarantee technical equivalence?",
        "No. Equivalence shown on the platform is sourcing and procurement assistance, not an engineering guarantee, a certification of interchangeability or an approval to substitute. Suitability for your specific application, service conditions and approvals remains your responsibility and should be confirmed by your engineering authority."],
      ["Where does technical information come from?",
        "From supplier and manufacturer submissions, published manufacturer literature, and ADIT's own data normalisation. Data may be incomplete or superseded, so where a decision matters you should consult the manufacturer-controlled document and the authoritative certificate source."],
      ["What happens if a product has a technical deviation?",
        "The deviation is shown against the specific parameter so you can see exactly what differs. You must review any displayed deviation before making an award. If you award with a deviation showing, you are accepting that deviation for your own account."],
    ],
  },
  {
    id: "buyers",
    title: "Buyers / RFQs",
    items: [
      ["How do I submit an RFQ?",
        "Either start from a product — the specification prefills from Spec Finder — or open the Submit an RFQ form directly. Add quantity and unit, delivery location, required date, Incoterm, certificate requirements and any notes, then submit. You will receive an RFQ number by email."],
      ["Can I upload drawings, BOQs and datasheets?",
        "Yes. You can attach drawings, bills of quantity, datasheets and other documents to an enquiry. Attachments are shared with the suppliers matched to that enquiry, so do not include information you are not permitted to disclose."],
      ["Can I request an exact brand?",
        "Yes. You can require a specific manufacturer and part number, and the enquiry will be routed accordingly."],
      ["Can I allow equivalent brands?",
        "Yes. If you allow equivalents, suppliers may also offer technically comparable products from other manufacturers, and every deviation is presented for your review before you decide."],
      ["How are suppliers selected/matched?",
        "Automatically, based on the supplier's registered product categories, brands and markets served, its verification status and its subscription allowance. Only suppliers whose registered capability matches the enquiry receive it — the enquiry is not broadcast to everyone."],
      ["How do I compare quotations?",
        "Quotations arrive in your buyer portal in a structured comparison view: technical parameters and deviations beside price, lead time, validity and commercial conditions. You can export the comparison to PDF or Excel for your internal approval file."],
      ["What is estimated landed cost?",
        "An indicative total that adds freight, duty, clearance and local delivery to the product price, so offers on different Incoterms can be compared on a like-for-like basis. These figures are estimates for comparison only, not a quotation of the final amount payable."],
      ["Who is responsible for the product after I award a Direct order?",
        "The supplier you awarded. In a Direct Purchase the contract is between you and that seller, and it is responsible for product accuracy, the correct part number, technical conformity, the documents and certificates it provides, delivery, warranty, returns and its own commercial terms."],
    ],
  },
  {
    id: "suppliers",
    title: "Suppliers",
    items: [
      ["Who can register as a supplier?",
        "Manufacturers, authorised distributors, stockists and established trading companies with a valid trade licence that can supply industrial materials and support them commercially and technically."],
      ["How do suppliers receive RFQs?",
        "Matching enquiries appear in your supplier portal and you are notified by email at your registered address. You only receive enquiries that match your registered categories, brands and markets, within your plan's allowance."],
      ["How do suppliers submit quotations?",
        "Open the enquiry in your portal and complete the structured quotation: price, currency, lead time, validity, Incoterm, country of origin, the exact product and part number offered, any technical deviation, and supporting documents. You can revise a quotation while the enquiry is still open."],
      ["Are suppliers guaranteed RFQs or orders?",
        "No. Neither registration nor a paid subscription guarantees any number of enquiries, quotations, awards, orders or revenue. Volume depends on buyer demand and on your own coverage, data quality, responsiveness and competitiveness."],
      ["What documents may suppliers need?",
        "Typically a valid trade licence, tax or VAT registration, evidence of brand authorisation where you claim it, and product-level documents such as datasheets and test or material certificates. Uploaded documents must be genuine and applicable to the product actually offered."],
      ["Can manufacturers add their products to Spec Finder?",
        "Yes. You can submit products individually or upload a spreadsheet of product data and price lists. Submissions go to an ADIT review queue and are published once approved, so the specification database stays reliable."],
    ],
  },
  {
    id: "direct-vs-managed",
    title: "Direct vs Managed by ADIT",
    items: [
      ["What is Direct Purchase?",
        "You award the enquiry to the supplier and contract with that supplier directly. ADIT's role is the introduction and the platform tools; the supply contract, invoice and performance sit between you and the seller."],
      ["What is Managed by ADIT?",
        "A route where you ask ADIT to handle the procurement itself, rather than contracting directly with the supplier. It is useful where you would prefer a single UAE counterparty, consolidated documentation or additional handling."],
      ["Does clicking Managed by ADIT automatically create an order?",
        "No. Clicking Managed by ADIT only submits a request. ADIT does not become the seller and does not accept the order automatically. A managed transaction exists only once ADIT has separately reviewed and expressly accepted that specific transaction and issued or accepted the appropriate commercial documentation."],
      ["Who invoices the buyer under Direct Purchase?",
        "The supplier invoices you directly, on its own commercial terms."],
      ["Who is responsible for warranty and returns?",
        "In a Direct Purchase, the supplier — for warranty, returns, replacements and any technical claim. Under an accepted Managed Procurement transaction, responsibility follows the commercial documentation ADIT has issued for that transaction."],
    ],
  },
  {
    id: "subscriptions",
    title: "Subscriptions",
    items: [
      ["What is the Founding/Free plan?",
        "A free supplier plan that lets you register, build your profile and product data, and respond to a limited number of live enquiries so you can evaluate the platform before paying anything."],
      ["What does Pro include?",
        "An expanded enquiry allowance and the full quoting workflow for a single company account, billed annually."],
      ["What does Enterprise include?",
        "Everything in Pro, plus team accounts so several colleagues can work under one company with their own logins and roles, for larger supply organisations. Billed annually."],
      ["Are subscriptions automatically renewed?",
        "Yes. Paid subscriptions renew automatically for a further term unless cancelled before the renewal date, and the renewal is charged to the payment method on file."],
      ["How can a supplier cancel?",
        "Cancel from the subscription area of your supplier portal, or email projects@aditintl.com. Cancellation takes effect at the end of the paid term and you keep paid access until then, after which the account reverts to Founding limits."],
      ["Are subscription fees refundable?",
        "Subscription fees are non-refundable except where ADIT states otherwise in writing or where a refund is required by law. Cancelling part-way through a term does not create a pro-rata refund."],
      ["Does subscribing guarantee sales?",
        "No. A subscription buys access to platform services and sourcing opportunities, not guaranteed business. No number of RFQs, orders or sales is promised."],
    ],
  },
  {
    id: "payments",
    title: "Payments",
    items: [
      ["How are subscription payments processed?",
        "Through a hosted checkout. Once payment succeeds, your subscription is activated on the platform automatically, your plan limits update, and a tax invoice is generated in your portal."],
      ["Is Stripe used?",
        "Yes. Stripe is our payment provider for supplier subscriptions, including renewals and refunds."],
      ["Does ADIT Connect store my complete credit-card information?",
        "No. Card details are handled by Stripe. ADIT Connect does not receive or store your full card number, expiry date or security code — only your subscription status, transaction references and the invoice data needed for accounting."],
    ],
  },
  {
    id: "support",
    title: "Support",
    items: [
      ["How do I contact ADIT Connect?",
        "Use the Get help form and the ADIT team will reply by email. Include RFQ or part numbers where relevant so we can answer faster."],
      ["What if I have a technical issue?",
        "Report it through Get help, choosing \"Technical problem with the platform\". Tell us what you were doing, what you expected and what happened, and include the page and any reference number."],
      ["What if I have a problem with a seller?",
        "In a Direct Purchase the commercial matter is between you and that seller, so raise it with them first. Tell us as well through Get help: we cannot decide a commercial dispute for you, but we do act on platform conduct — false documents, misrepresented products or abuse can lead to suspension."],
    ],
  },
];

const Item = ({ q, a, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E6EBF0] last:border-0">
      <button
        type="button"
        data-testid={testId}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start justify-between gap-4 py-4 text-left transition-colors hover:text-[#147D78]"
      >
        <span className="font-display text-sm font-semibold text-[#102A43] md:text-base">{q}</span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-[#8899A8] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <p className="pb-5 pr-8 text-sm leading-relaxed text-[#48606F]" data-testid={`${testId}-answer`}>
          {a}
        </p>
      )}
    </div>
  );
};

export default function Faqs() {
  return (
    <div data-testid="faqs-page">
      <LegalHero
        testId="faqs-hero"
        eyebrow="Help centre"
        title="Frequently asked questions"
        intro="How ADIT Connect works for buyers and for suppliers — specification search, RFQs,
          awards, subscriptions and support."
      />

      <div className="mx-auto max-w-[900px] px-5 py-12">
        <nav className="flex flex-wrap gap-x-5 gap-y-2 border-b border-[#DFE6ED] pb-6 text-sm">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`faq-jump-${s.id}`}
              className="font-medium text-[#147D78] hover:underline"
            >
              {s.title}
            </a>
          ))}
        </nav>

        <div className="space-y-10 pt-8">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`faq-section-${s.id}`}>
              <h2 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#D9A441]">
                {s.title}
              </h2>
              <div className="mt-2 rounded-[12px] border border-[#DFE6ED] bg-white px-5 shadow-[0_1px_2px_rgba(16,42,67,0.04)]">
                {s.items.map(([q, a], i) => (
                  <Item key={q} q={q} a={a} testId={`faq-${s.id}-${i}`} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-[12px] bg-[#102A43] px-6 py-7 text-center">
          <p className="font-display text-lg font-bold text-white">Still need an answer?</p>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#bcccdc]">
            Send it to the ADIT team and we will reply by email.
          </p>
          <Link
            to="/get-help"
            data-testid="faq-get-help-link"
            className="mt-5 inline-flex items-center rounded-full bg-[#D9A441] px-6 py-2.5 text-sm font-semibold text-[#102A43] transition-colors hover:bg-[#e6b75f]"
          >
            Get help
          </Link>
        </div>

        <LegalFooterNav current="/faqs" />
      </div>
    </div>
  );
}
